import { getTenantFromHostname } from "shared/utils/tenant-utils";

/**
 * This file serves as a type definition for the config.js and to provide a
 * .get() function to retrieve the config from the window object.
 */
export const SUPPORT_EMAIL = "support@viaduct.ai";

export const API_CURRENT_VERSION = "v1";

interface HeapAnalyticsConfig {
  id: string;
}

interface AnalyticsConfig {
  heap?: HeapAnalyticsConfig;
}

type AuthScopes = "openid" | "email" | "offline_access";

interface AuthConfig {
  name: string;
  idp?: string;
  issuer: string;
  clientId: string;
  scopes: AuthScopes[];
  postLogoutRedirectUri?: string;
}

interface DataExplorerConfig {
  url: string;
}

interface SingleIDPConfig {
  unauthenticatedRedirect?: string;
  optionalAutoLogin?: boolean;
}

interface FeatureFlagsConfig {
  launchDarklyClientId: string;
  anonymizeEmail?: boolean;
}

interface SentryConfig {
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  dsn?: string;
}

interface AppConfig {
  analytics: AnalyticsConfig;
  featureFlags: FeatureFlagsConfig;
  auth: AuthConfig;
  isIframed?: boolean;
  instance: string;
  dataExplorer?: DataExplorerConfig;
  singleIDPLogin?: SingleIDPConfig;
  sentry?: SentryConfig;
  apiBaseUrl?: string;
  aiBaseUrl?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

export const get = (): AppConfig => {
  const config = window.__APP_CONFIG__;
  if (!config) {
    throw new Error("Missing required configuration file.");
  }

  if (
    !config.instance ||
    !config.auth?.name ||
    !config.auth?.issuer ||
    !config.auth?.clientId ||
    !config.auth?.scopes ||
    !config.featureFlags?.launchDarklyClientId
  ) {
    throw new Error(
      "Missing required configuration fields. Current config is\n" +
        JSON.stringify(config)
    );
  }

  return config;
};

export const isInstanceManagement = (): boolean => {
  const { instance } = get();

  return instance === getTenantFromHostname();
};
